import { ThunkAction, ThunkDispatch } from 'redux-thunk';

import { ApplicationAction } from 'state/actions/types/applicationActionsTypes';
import { ArticleAction } from 'state/actions/types/articleActionsTypes';
import { AuthorPageAction } from 'state/actions/types/authorPageActionsTypes';
import { ApplicationUIAction } from 'state/actions/types/applicationUIActionsTypes';
import { BusinessOnboardingPageAction } from 'state/actions/types/businessOnboardingPageActionsTypes';
import { CampaignPageAction } from 'state/actions/types/campaignPageActionsTypes';
import { FormAction } from 'state/actions/types/formActionsTypes';
import { GenericPageAction } from 'state/actions/types/genericPageActionsTypes';
import { GlobalSettingsAction } from 'state/actions/types/globalSettingsActionsTypes';
import { JobPostingsAction } from 'state/actions/types/jobPostingsActionsTypes';
import { MarketingPageAction } from 'state/actions/types/marketingPageActionsTypes';
import { ShowcasePageAction } from 'state/actions/types/showcasePageActionsTypes';
import { SurveyAction } from 'state/actions/types/surveyActionsTypes';
import { TagAction } from 'state/actions/types/tagActionsTypes';
import { TopicAction } from 'state/actions/types/topicActionsTypes';

import { ApplicationUIReducer } from 'state/reducers/applicationUI';
import { ArticleReducer } from 'state/reducers/article';
import { BusinessOnboardingPageReducer } from 'state/reducers/businessOnboardingPage';
import { CampaignPageReducer } from 'state/reducers/campaignPage';
import { TagReducer } from 'state/reducers/tag';
import { TopicReducer } from 'state/reducers/topic';
import { GenericPageReducer } from 'state/reducers/genericPage';
import { GlobalSettingsReducer } from 'state/reducers/globalSettings';
import { JobPostingsReducer } from 'state/reducers/jobPostings';
import { MarketingPageReducer } from 'state/reducers/marketingPage';
import { StatusReducer } from 'state/reducers/status';
import { AuthorPageReducer } from 'state/reducers/authorPage';
import { ShowcasePageReducer } from 'state/reducers/showcasePage';

import {
  CalloutColor,
  EmphasizeModuleColor,
  GeneralModuleColor,
  SplitCtaColor,
} from 'constants/ColorScheme';
import { Color } from 'styled/theme/colors';
import { FontSizes, Fonts } from 'constants/Typography';

declare global {
  interface Window {
    google_optimize: {
      get: (experimentId: string) => string;
    };
    gtag: (
      type: string,
      eventName: string,
      payload: {
        name: string;
        callback: Function;
      }
    ) => void;
  }
}

export type Action =
  | ApplicationAction
  | ApplicationUIAction
  | ArticleAction
  | AuthorPageAction
  | BusinessOnboardingPageAction
  | FormAction
  | GenericPageAction
  | GlobalSettingsAction
  | JobPostingsAction
  | MarketingPageAction
  | CampaignPageAction
  | ShowcasePageAction
  | HydrateAction
  | SurveyAction
  | TagAction
  | TopicAction;

export interface AccordionListItem {
  id: string;
  button: {
    label: string;
    description: string;
  };
  content: string;
}

export interface AccordionListModule {
  type: 'accordionList';
  id: string;
  moduleHeader: ModuleHeader;
  listItems: AccordionListItem[];
  button: {
    label: string;
    url: string;
  };
  moduleOptions: ModuleOptions;
  bottomDescription: PortableText;
}

export interface Article {
  id: string;
  title: string;
  slug: string;
  type: 'article';
  meta: ArticleMeta;
  hero: ArticleHero;
  content: ArticleContent;
  seo: ArticleSeoSettings;
}

export type ArticleBody =
  | AccordionListModule
  | ArticleButton
  | ArticleResearch
  | ArticleText
  | CarouselModule
  | EmphasisLinkCardModule
  | HighlightedText
  | ImageCardsModule
  | LargeTextModule
  | LinkCardsModule
  | LinkCardWithImageModule
  | LinksListModule
  | NumberedListWithImageModule
  | QuoteModule
  | SingleBlockCtaModule
  | SplitCtaModule
  | null;

export interface ArticleButton {
  type: 'articleButton';
  id: string;
  sideNav: ArticleSideNavOption;
  label: string;
  link: string;
  logoIsActive: boolean;
}

export interface ArticleCallout {
  id: string;
  type: 'articleCallout';
  color: CalloutColor;
  variant: 'right' | 'bottom';
  infoSectionText: PortableText;
  supportingSectionIsActive: boolean;
  supportingSectionText: PortableText;
  supportingSectionImage: Image;
  renderSupportingSectionWider: boolean;
}

export interface ArticleItem extends ArticleLink {}

export interface ArticleContent {
  articleUserStories: ArticleUserStory[];
  overviewSlug: string;
  overviewLabel: string;
  overview: PortableText;
  bodyModules: ArticleBody[];
  references: PortableText;
  moreArticles: (ArticleLink | null)[];
  articleRating: number;
  hideUserStory: boolean;
}

export interface ArticleGenericPage {
  id: string;
  type: 'articleGenericPage';
  title: string;
  slug: string;
  showIntercom: boolean;
  bodyModules: ArticleBody[];
  date: string;
}

export interface ArticleHero {
  image: Image;
  subtitle: string;
  color: EmphasizeModuleColor;
}

export interface ArticleImage {
  id: string;
  type: 'articleImage';
  isFullWidth: boolean;
  caption: string;
  desktopImage: Image;
  mobileImage: Image;
}

export interface ArticleLink {
  id: string;
  type: 'article';
  title: string;
  slug: string;
  topic: {
    title: string;
    slug: string;
  };
  heroImage: Image;
  subtitle: string;
  moduleTitle: string;
  moduleDescription: string;
  moduleImage: Image;
}

export interface ArticleRating {
  rating: number;
}

export interface ArticleResearch {
  id: string;
  type: 'research';
  surveyMonkeyPageId: string;
  surveyUrl: string;
  title: string;
  description: string;
  image: Image;
  color: GeneralModuleColor;
}

export interface ArticleVideo extends Video {
  id: string;
  type: 'articleVideo';
  isFullWidth: boolean;
  caption: string;
}

export interface ArticleMeta {
  author: Author | null;
  reviewer: Author | null;
  publishedDate: string;
  modifiedDate: string;
  tags: Tag[];
  topic: {
    title: string;
    slug: string;
  };
}

export interface ArticleSideNavOption {
  id: string;
  label: string;
  slug: string;
}

export interface ArticleSeoSettings extends SeoSettings {
  keywords: string[];
}

export interface ArticleText {
  type: 'articleText';
  id: string;
  sideNav: ArticleSideNavOption;
  text: PortableText;
}

export interface ArticleUserStory {
  story: string;
  name: string;
  title: string;
  id: string;
  date: string;
  likes: number;
}

export interface Asset {
  id: string;
  alt: string;
}

export interface Author extends TeamMember {
  honorifics: string;
  specialty: string;
  bio: PortableText;
  links: ExternalLink[];
  educationAndTraining: EducationInfo[];
  practiceNames: PracticeName[];
  certifications: PortableText;
  awards: PortableText;
  publications: PortableText;
}

export interface AuthorPage extends Author {
  articles: (ArticleLink | null)[];
}

export interface BusinessOnboardingPage {
  type: 'businessOnboardingPage';
  title: string;
  showIntercom: boolean;
  description: PortableText;
  formTitle: string;
  demoVideo: Video;
  seo: SeoSettings;
}

export interface CarouselModule {
  type: 'carousel';
  id: string;
  moduleHeader: ModuleHeader;
  moduleOptions: ModuleOptions;
  carouselItems: CarouselItem[];
}

export type CarouselItem =
  | ArticleItem
  | ImageItem
  | QuoteItem
  | TextItem
  | TopicItem
  | VideoItem
  | null;

export interface CarouselItemOption {
  id: string;
  button: Button;
  description: string;
  subtitle: string;
  title: string;
}

export interface ContentHubCategory {
  id: string;
  title: string;
  slug: string;
  order: string;
  parentCategoryId: string;
  hero: PageHero;
  modules: Module[];
}

export interface BusinessOnboarding {
  inputFields: InputField[];
  popUpTitle: string;
  popUpDescription: string;
  popUpVideo: Video;
}

export interface Button {
  label: string;
  link: string;
}

export interface Callout {
  isActive: boolean;
  color: CalloutColor;
  infoSectionIsActive: boolean;
  variant: 'right' | 'left' | 'bottom';
  infoSectionImage: Image;
  infoSectionText: PortableText;
  supportingSectionImage: Image;
  supportingSectionText: PortableText;
}

export interface CampaignPage {
  id: string;
  type: 'campaignPage';
  title: string;
  slug: string;
  description: PortableText;
  formTitle: string;
  formLeadSource: string;
  formLeadSourceOtherC: string;
  demoVideo: Video;
  showIntercom: boolean;
  seo: SeoSettings;
}

export type Dispatch = ThunkDispatch<GlobalState, undefined, Action>;

export interface DropdownOption {
  value: string;
  label: string;
}

export interface DynamicRoute {
  as: string;
  href: string;
}

export interface EducationInfo {
  id: string;
  details: string;
  logo: Image;
}

export interface ErrorPage {
  title: string;
  description?: string;
  link?: {
    label: string;
    url: string;
  };
  image?: Image;
}

export interface EmphasisLinkCardModule {
  type: 'emphasisLinkCard';
  id: string;
  content: LinkCard;
  contentTitleAlignment: 'left' | 'center';
  moduleOptions: ModuleOptions;
}

export interface ExternalLink {
  id: string;
  label: string;
  url: string;
}

export interface Footer {
  social: ExternalLink[];
  menu: FooterMenu[];
  termsSlug: string;
  privacySlug: string;
  cookiesSlug: string;
  footerImage: FooterImage | null;
}

export interface FooterMenu {
  title: string;
  list: InternalLink[];
}

export interface FooterSocial {
  label: string;
  link: string;
}

export interface FooterImage {
  image: Image;
  alternateImage: Image;
  text: string;
}

export interface FormValues {
  [title: string]: string;
}

export interface FourColumnVideoModule {
  id: string;
  type: 'fourColumnVideo';
  moduleHeader: ModuleHeader;
  moduleOptions: ModuleOptions;
  contents: FourColumnVideoModuleItem[];
}

export interface FourColumnVideoModuleItem {
  id: string;
  title: string;
  vimeoId: string;
  image: Image;
  customPopUpTitle: string;
  description: string;
  buttonLabel: string;
}

export interface InputField {
  label: string;
  title: string;
  isRequired: boolean;
  type: InputFieldType;
  options: string[];
}

export type InputFieldType = 'email' | 'text' | 'tel' | 'number' | 'textarea';

export interface GeneralInquiry {
  inputFields: InputField[];
  sectionTitle: string;
  sectionDescription: PortableText;
  formDescription: string;
  submitFormToEmail: boolean;
}

export interface GeneralInquiryModule {
  type: 'generalInquiry';
  id: string;
}

export interface GenericPage {
  id: string;
  type: 'genericPage';
  title: string;
  slug: string;
  showIntercom: boolean;
  hero: PageHero;
  modules: Module[];
  seo: SeoSettings;
}

export interface GlobalState {
  applicationUI: ApplicationUIReducer;
  article: ArticleReducer;
  businessOnboardingPage: BusinessOnboardingPageReducer;
  campaignPage: CampaignPageReducer;
  tag: TagReducer;
  topic: TopicReducer;
  genericPage: GenericPageReducer;
  globalSettings: GlobalSettingsReducer;
  jobPostings: JobPostingsReducer;
  marketingPage: MarketingPageReducer;
  status: StatusReducer;
  authorPage: AuthorPageReducer;
  showcasePage: ShowcasePageReducer;
}

export interface HighlightedText {
  type: 'highlightedText';
  id: string;
  sideNav: ArticleSideNavOption;
  text: PortableText;
}

interface HydrateAction {
  type: '__NEXT_REDUX_WRAPPER_HYDRATE__';
  payload: any;
}

export interface Image {
  id: string;
  src: string;
  alt: string;
  crop: ImageCrop | null;
  hotspot: object | null;
  metadata: {
    dimensions: ImageDimensions;
  } | null;
}

export interface ImageItem extends CarouselItemOption {
  type: 'imageItem';
  image: Image;
}

export interface ImageDimensions {
  width: number;
  height: number;
}

export interface ImageCard {
  id: string;
  title: string;
  description?: string;
  image: Image;
}

export interface ImageCardsModule {
  type: 'imageCards';
  id: string;
  title: string;
  imageCards: ImageCard[];
  moduleOptions: ModuleOptions;
}

export interface ImageCrop {
  bottom?: number;
  left?: number;
  top?: number;
  right?: number;
}

export interface InternalLink {
  id: string;
  label: string;
  slug: string;
}

export interface JobPosting {
  description: string;
  location: string;
  title: string;
  link: string;
  id: string;
}

export interface JobPostings {
  [department: string]: JobPosting[];
}

export interface JobPostingsModule {
  type: 'jobPostings';
  id: string;
  moduleHeader: ModuleHeader;
  button: {
    label: string;
    url: string;
  };
  moduleOptions: ModuleOptions;
  bottomDescription: PortableText;
}

export interface InstagramFeedImageItem {
  id: string;
  src: string;
  link: string;
}

export interface LargeTextModule {
  type: 'largeText';
  id: string;
  text: string;
  desktopFontSize: FontSizes.xl | FontSizes.xxl;
  moduleOptions: ModuleOptions;
}

export interface LinksListItem {
  type: 'manualContent' | 'articleRef';
  id: string;
  title: string;
  description: string;
  url: string;
  buttonLabel: string;
}

export interface LinksListModule {
  type: 'linksList';
  id: string;
  moduleOptions: ModuleOptions;
  moduleHeader: ModuleHeader;
  contents: LinksListItem[];
}
export interface LinkCard {
  id: string;
  title: string;
  description?: string;
  button: {
    ariaLabel?: string;
    label: string;
    url: string;
    variant: 'underline-white' | 'underline-black' | 'primary';
  };
}

export interface LinkCardWithType extends LinkCard {
  type: string;
}

export interface LinkCardsModule {
  type: 'linkCards';
  id: string;
  linkCards: LinkCardWithType[];
  moduleOptions: ModuleOptions;
}

export interface LinkCardWithImageModule {
  type: 'linkCardWithImage';
  id: string;
  linkCard: LinkCard;
  image: Image;
  desktopImagePosition: 'left' | 'right';
  moduleOptions: ModuleOptions;
}

export type Module =
  | AccordionListModule
  | CarouselModule
  | EmphasisLinkCardModule
  | GeneralInquiryModule
  | ImageCardsModule
  | JobPostingsModule
  | LargeTextModule
  | LinkCardsModule
  | LinkCardWithImageModule
  | LinksListModule
  | InstagramFeedModule
  | NumberedListWithImageModule
  | ThreeColumnInfoModule
  | QuoteModule
  | SingleBlockCtaModule
  | SplitCtaModule
  | TagsIndexModule
  | TeamListModule
  | TextModule
  | VideoModule
  | FourColumnVideoModule
  | null;

export interface ModuleHeader {
  title: string;
  description: string;
}

export interface ModuleOptions {
  color: GeneralModuleColor | EmphasizeModuleColor | SplitCtaColor;
  showBorderTop: boolean;
  showBorderBottom: boolean;
  callout: Callout;
}

export interface InstagramFeedModule {
  type: 'instagramFeed';
  id: string;
  title: string;
  buttonLabel: string;
  buttonUrl: string;
}

export interface NavList {
  [slug: string]: NavListItem;
}

export interface NavListItem {
  children: NavSublist[];
  order: number;
  label: string;
  slug: string;
}

export interface NavSublist {
  parent: string;
  label: string;
  slug: string;
}

export interface NotificationBar {
  isActive: boolean;
  text: string;
}

export interface NumberedListItem {
  id: string;
  title: string;
  description: string;
  image?: Image;
}

export interface NumberedListWithImageModule {
  type: 'numberedListWithImage';
  id: string;
  moduleOptions: ModuleOptions;
  numberOptions: {
    bgColor: SplitCtaColor;
    color: EmphasizeModuleColor | null;
  };
  topImage: Image;
  topImageBottomPosition: number;
  contents: NumberedListItem[];
}

export interface MarketingPage {
  id: string;
  showIntercom: boolean;
  hero: MarketingPageHero;
  modules: Module[];
  seo: SeoSettings;
}

export interface MarketingPageHero {
  title: string;
  description: string;
  color: EmphasizeModuleColor;
  desktopImage: Image;
  mobileImage: Image;
  formTitle: string;
  formDescription: PortableText;
  formFields: InputField[];
}

export interface OrganizedTopic {
  topics: TopicLink[];
  topicsBySlug: {
    [topicSlug: string]: TopicLink;
  };
  topicsById: {
    [topicId: string]: TopicLink;
  };
  topicsByParentSlug: {
    [parentSlug: string]: TopicLink[];
  };
}

export interface PageHero {
  variant: string;
  title: string;
  description: string;
  color: EmphasizeModuleColor;
  desktopImage: Image;
  mobileImage: Image;
  desktopImageWidth: number;
  mobileImageWidth: number;
  modules: Module[];
  buttonLink: string;
  buttonLabel: string;
}

export interface PopUp {
  isActive: boolean;
  text: PortableText;
  continueButtonLabel: string;
  secondaryButtonLabel: string;
  secondaryButtonLink: string;
}

export type PortableText = object[];

export interface PracticeName {
  id: string;
  name: string;
  logo: Image;
  link: {
    label: string;
    url: string;
  };
}

export interface QuoteItem {
  type: 'quoteItem';
  id: string;
  quote: string;
  attribution: PortableText;
  description: string;
  button: Button;
}

export interface QuoteModule {
  type: 'quote';
  id: string;
  quote: PortableText[];
  attribution: string;
  moduleOptions: ModuleOptions;
}

export type SeoSettings = {
  title: string;
  description: string;
  image: Image;
};

export interface ShowcasePage {
  id: string;
  type: 'showcasePage';
  title: string;
  slug: string;
  hero: PageHero;
  modules: Module[];
}

export interface SingleBlockCtaModule {
  type: 'singleBlockCta';
  id: string;
  text: string;
  link: string;
  slug: string;
  variant: 'with-arrow' | 'underlined';
  size: 'medium' | 'large';
  moduleOptions: ModuleOptions;
}

export interface SplitCtaModule {
  type: 'splitCta';
  id: string;
  title: string;
  description: string;
  downloadLink: string;
  slug: string;
  moduleOptions: ModuleOptions;
}

export enum Status {
  IDLE = 'IDLE',
  PENDING = 'PENDING',
  REJECTED = 'REJECTED',
  FULFILLED = 'FULFILLED',
}

export interface SurveyChoice {
  id: string;
  text: string;
}

export interface SurveyPages {
  [pageId: string]: SurveyPage;
}

export interface SurveyPage {
  id: string;
  title: string;
  description: string;
  questions: SurveyQuestion[];
}

export interface SurveyQuestion {
  id: string;
  heading: string;
  choices: SurveyChoice[];
}

export interface SurveyResponse {
  [questionId: string]: string | null;
}

export interface SurveyResponseRequestBody {
  pages: {
    id: string;
    questions: SurveyResponseRequestBodyQuestion[];
  }[];
}

export interface SurveyResponseRequestBodyQuestion {
  id: string;
  answers: {
    choice_id: string;
  }[];
}

export interface Tag {
  id: string;
  title: string;
  topic: TopicLink;
}

export interface TagsIndexModule {
  type: 'tagsIndex';
  id: string;
  moduleHeader: ModuleHeader;
}

export interface TeamListModule {
  type: 'teamList';
  id: string;
  moduleHeader: ModuleHeader;
  contents: TeamListModuleContent[];
  moduleOptions: ModuleOptions;
}

export interface TeamListModuleContent extends TeamMember {
  type: string;
}

export interface TeamMember {
  id: string;
  firstName: string;
  lastName: string;
  slug: string;
  jobTitle?: string;
  image: Image;
}

export interface TeamMemberPage extends TeamMember {
  modules: Module[];
}

export interface TextItem extends CarouselItemOption {
  type: 'textItem';
}

export interface TextModule {
  type: 'textModule';
  id: string;
  anchor: string;
  title: string;
  moduleHeader: ModuleHeader;
  button: {
    label: string;
    url: string;
    variant: 'underline-white' | 'underline-black' | 'primary';
  };
  titleFontSize: FontSizes.lg | FontSizes.xl;
  contents: Array<PortableText | Image>;
  contentFont: Fonts;
  contentFontSize: FontSizes.sm | FontSizes.md;
  contentTitleAlignment: 'left' | 'center';
  contentAlignment: 'left' | 'center';
  contentIsTwoColumnOnDesktop: boolean;
  separateTitleAndContentOnDesktop: boolean;
  contentIsFullWidth: boolean;
  moduleOptions: ModuleOptions;
}

export interface ThreeColumnInfoModule {
  type: 'threeColumnInfo';
  id: string;
  moduleHeader: ModuleHeader;
  button: {
    label: string;
    url: string;
    variant: 'underline-white' | 'underline-black' | 'primary';
  };
  numberOptions: {
    displayNumber: boolean;
    bgColor: 'white' | 'gray-lightest' | 'blue-darker';
    color: Color;
  };
  contentAlign: 'left' | 'center';
  isTwoColumnOnMobile: boolean;
  maxItemsPerPage: number;
  tabs: string[];
  contents: ThreeColumnInfoModuleContents;
  moduleOptions: ModuleOptions;
}

export type ThunkResult<R> = ThunkAction<R, GlobalState, undefined, Action>;

export type ThreeColumnInfoModuleContents = {
  [tab: string]: ThreeColumnInfoModuleContent[];
};

export type ThreeColumnInfoModuleContent =
  | ThreeColumnModuleManualContent
  | ThreeColumnModuleArticleLink
  | ThreeColumnModuleTopicLink
  | null;

export interface ThreeColumnModuleArticleLink {
  type: 'article';
  id: string;
  tabs: string[];
  article: ArticleLink;
}

export interface ThreeColumnModuleTopicLink {
  type: 'topic';
  id: string;
  tabs: string[];
  topic: TopicLink;
}

export interface ThreeColumnModuleManualContent {
  type: 'manualContent';
  id: string;
  url: string;
  ariaLabel: string;
  image: Image;
  title: string;
  description: string;
  button: {
    label: string;
    ariaLabel: string;
    url: string;
  };
  tabs: string[];
}

export interface Topic {
  id: string;
  type: 'topic';
  title: string;
  slug: string;
  order: number;
  parentTopicId: string;
  hero: PageHero;
  modules: Module[];
  seo: SeoSettings;
  moduleImage: Image;
}

export interface TopicItem extends TopicLink {}

export interface TopicLink {
  id: string;
  type: 'topic';
  title: string;
  slug: string;
  order: number;
  moduleImage: Image;
  moduleDescription: string;
  parentTopicId: string;
}

export interface Video {
  vimeoId: string;
  title?: string;
}

export interface VideoItem extends CarouselItemOption {
  type: 'videoItem';
  video: Video;
}

export interface VideoModule extends Video {
  id: string;
  type: 'videoModule';
}
